import { Link } from "react-router-dom";
import { Phone, Clock, AlertCircle, Check, ArrowRight } from "lucide-react";
import { CLINIC } from "../lib/api";
import AppointmentForm from "../components/AppointmentForm";
import FAQ from "../components/FAQ";
import CTASection from "../components/CTASection";

export default function Urgencias() {
  const faqs = [
    { q: "¿Atienden urgencias el mismo día?", a: "Sí. Si llamas antes de las 12:00, te atendemos el mismo día. Reservamos huecos diarios para emergencias." },
    { q: "¿Qué se considera una urgencia dental?", a: "Dolor intenso persistente, flemón o inflamación facial, rotura o pérdida de pieza dental, pérdida de empaste o corona, sangrado abundante tras traumatismo, o pérdida total de la pieza (avulsión)." },
    { q: "¿Atienden a turistas y pacientes internacionales?", a: "Sí. Tenemos personal que habla español, catalán, inglés y francés. Atendemos muchos pacientes turistas durante toda la temporada." },
    { q: "¿Qué hago si se me cae un diente por un golpe?", a: "Conserva la pieza en leche o suero fisiológico (nunca agua). Evita tocar la raíz. Acude inmediatamente. El tiempo es crítico para un posible reimplante." },
  ];
  return (
    <div data-testid="urgencias-page">
      <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-teal-50">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
                <div className="relative w-2 h-2 rounded-full bg-amber-500 pulse-dot" />
                <span className="text-xs font-bold text-amber-800 uppercase">Urgencias dentales</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Urgencias dentales en <span className="underline-accent">Calafell</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Un dolor dental no espera. Si tienes un problema urgente en Calafell o alrededores,
                te atendemos hoy mismo. Llámanos ahora.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a href={`tel:${CLINIC.phone}`} className="btn-primary bg-amber-500 hover:bg-amber-600 text-slate-900 px-8 py-4 text-base font-bold" data-testid="urgencias-phone-cta" style={{backgroundColor: "#F59E0B"}}>
                  <Phone className="w-5 h-5" /> Llamar ahora
                </a>
                <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                  WhatsApp
                </a>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-200">
                <Clock className="w-5 h-5 text-teal-700 shrink-0" />
                <div className="text-sm">
                  <strong className="text-slate-900">Horario ampliado:</strong>{" "}
                  <span className="text-slate-600">Lun-Jue 10-18h · Mié y Vie 10-14h</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-amber-100">
              <div className="flex items-center gap-2 mb-5 text-amber-600">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-semibold">Solicita atención urgente</span>
              </div>
              <AppointmentForm defaultService="Urgencias dentales" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-max">
          <div className="max-w-3xl mb-14">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              ¿Cuándo debes acudir a urgencias dentales?
            </h2>
            <p className="text-lg text-slate-600">
              Si tienes cualquiera de estas señales, no esperes. Contacta con nosotros.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Dolor dental intenso que no cede",
              "Inflamación facial o flemón",
              "Rotura o pérdida de pieza dental",
              "Pérdida de empaste o corona",
              "Sangrado bucal abundante",
              "Traumatismo dental (golpes)",
              "Infección dental visible",
              "Dolor tras una intervención",
              "Pérdida de provisional",
            ].map((item, i) => (
              <div key={i} className="bg-amber-50 rounded-2xl p-5 flex items-start gap-3 border border-amber-100" data-testid={`urgencia-type-${i}`}>
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <span className="text-slate-800 font-medium text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="container-max">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 leading-tight text-center">
              Qué hacer mientras llegas a la clínica
            </h2>
            <p className="text-lg text-slate-600 mb-12 text-center">
              Sigue estos consejos mientras te diriges a nuestra clínica.
            </p>
            <div className="space-y-4">
              {[
                { t: "Enjuaga con agua tibia", d: "Un enjuague suave con agua tibia y sal puede aliviar el dolor y limpiar la zona." },
                { t: "Aplica frío externamente", d: "Una compresa fría sobre la mejilla reduce la inflamación." },
                { t: "Conserva fragmentos dentales", d: "Si se te cae un diente o fragmento, guárdalo en leche o suero." },
                { t: "Evita analgésicos sin supervisión", d: "No coloques aspirina directamente sobre el diente ni la encía." },
                { t: "Llámanos inmediatamente", d: "Describe tu situación y te daremos instrucciones específicas." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-700 flex items-center justify-center shrink-0 text-white font-bold text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{item.t}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ items={faqs} title="Preguntas sobre urgencias dentales" />
      <CTASection />
    </div>
  );
}
